import { Injectable } from '@angular/core';
import { Observable, from, map, shareReplay, switchMap, of, first, tap } from 'rxjs';
import { COLLECTIONS } from '../../core.constants';
import { EditionType, Entity, Id, Translation } from '../../models/common.model';
import { FirestoreService } from '../firestore.service';
import { deleteField, updateDoc } from '@angular/fire/firestore';

export interface Category {
  id: string;
  translations: Translation;
  types: EditionType[];
  suckMyTrackColor: string;
  name: string;
  order?: number;
}

export interface CategoriesDocument extends Entity {
  [key: string]: any; // Para permitir propiedades dinámicas
}

@Injectable({
  providedIn: 'root',
})
export class CategoriesMasterService {
  // Obtenemos el documento directamente
  categoriesDoc$ = this.firestore
    .getDocument<CategoriesDocument>(COLLECTIONS.MASTERS, COLLECTIONS.MASTERS_CATEGORIES_DOC)
    .pipe(shareReplay(1));

  // Transformamos el documento en un array de categorías
  categories$ = this.categoriesDoc$.pipe(
    map((doc) => {
      const result: Category[] = [];
      // Excluimos las propiedades que pertenecen a la Entity
      const excludeProps = ['id', 'createdAt', 'updatedAt', 'deleted'];

      // Para cada propiedad que no sea de Entity ni empiece con $, considerarla una categoría
      Object.keys(doc || {})
        .filter((key) => !excludeProps.includes(key) && !key.startsWith('$'))
        .forEach((categoryId) => {
          if (doc[categoryId] && typeof doc[categoryId] === 'object') {
            const categoryData = doc[categoryId];
            if (categoryData.translations && categoryData.types) {
              result.push({
                id: categoryId,
                translations: categoryData.translations,
                types: categoryData.types,
                suckMyTrackColor: categoryData.suckMyTrackColor,
                name: categoryData.name,
                order: categoryData.order ?? 999,
              });
            }
          }
        });

      // Ordenar por el campo order
      result.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

      return result;
    }),
    shareReplay(1),
  );

  marocCategories$ = this.categories$.pipe(
    map((categories) => categories.filter((category) => category.types.includes('maroc'))),
  );

  tunisieCategories$ = this.categories$.pipe(
    map((categories) => categories.filter((category) => category.types.includes('tunisie'))),
  );

  adminCategoriesSelect$ = this.categories$.pipe(
    map((categories) =>
      categories.map((category) => ({
        value: category.id,
        label: category.name,
      })),
    ),
  );

  adminMarocCategoriesSelect$ = this.categories$.pipe(
    map((categories) =>
      categories
        .filter((category) => category.types.includes('maroc'))
        .map((category) => ({
          value: category.id,
          label: category.name,
        })),
    ),
  );

  adminTunisieCategoriesSelect$ = this.categories$.pipe(
    map((categories) =>
      categories
        .filter((category) => category.types.includes('tunisie'))
        .map((category) => ({
          value: category.id,
          label: category.name,
        })),
    ),
  );

  constructor(private firestore: FirestoreService) {}

  getCategory(id: Id): Observable<Category | undefined> {
    return this.categories$.pipe(map((categories) => categories.find((c) => c.id === id)));
  }

  getAllCategories(): Observable<Category[]> {
    return this.categories$;
  }

  saveCategory(category: Category): Promise<void> {
    const updateData = {
      [category.id]: {
        id: category.id,
        translations: category.translations,
        types: category.types,
        name: category.name,
        suckMyTrackColor: category.suckMyTrackColor || '#000000', // Valor por defecto si no se proporciona
        order: category.order ?? 999,
      },
    };
    return this.firestore.createOrUpdate<CategoriesDocument>(
      COLLECTIONS.MASTERS,
      COLLECTIONS.MASTERS_CATEGORIES_DOC,
      updateData,
    );
  }

  async updateCategoriesOrder(categories: Category[]): Promise<void> {
    const updateData: Record<string, number> = {};
    categories.forEach((category, index) => {
      updateData[`${category.id}.order`] = index;
    });
    const docRef = this.firestore.getDocumentRef(COLLECTIONS.MASTERS, COLLECTIONS.MASTERS_CATEGORIES_DOC);
    return updateDoc(docRef, updateData);
  }

  deleteCategory(categoryId: string): Promise<void> {
    const docRef = this.firestore.getDocumentRef(COLLECTIONS.MASTERS, COLLECTIONS.MASTERS_CATEGORIES_DOC);
    return updateDoc(docRef, { [categoryId]: deleteField() });
  }

  // Método temporal para limpiar campos incorrectos - eliminar después de usar
  async cleanupInvalidOrderFields(): Promise<void> {
    const docRef = this.firestore.getDocumentRef(COLLECTIONS.MASTERS, COLLECTIONS.MASTERS_CATEGORIES_DOC);
    return updateDoc(docRef, {
      'ADVENTUREC2.order': deleteField(),
      'ADVENTUREC3.order': deleteField(),
      'ADVENTUREC4.order': deleteField(),
    });
  }
}
